const CATALOGUE_URL = new URL('./afroqueen-catalogue-preview.json', import.meta.url);
const CATALOGUE_PATH = '/collections/all';
const MAX_RESULTS = 8;

let cataloguePromise;
let searchTimer;

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function catalogueUrl(query) {
  const url = new URL(CATALOGUE_PATH, window.location.origin);
  if (query) url.searchParams.set('aq-search', query);
  return url.toString();
}

function loadCatalogue() {
  if (!cataloguePromise) {
    cataloguePromise = fetch(CATALOGUE_URL, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Catalogue HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (!Array.isArray(payload.products)) throw new Error('Catalogue products missing.');
        return payload.products;
      });
  }
  return cataloguePromise;
}

function productText(product) {
  return normalize([
    product.title,
    product.vendor,
    product.productType,
    product.collection,
    ...(product.variants || []).flatMap((variant) => [variant.label, variant.sku]),
  ].join(' '));
}

function productImage(product) {
  if (product.imageUrl) return product.imageUrl;
  const primary = (product.images || []).find((image) => image?.filename === product.primaryImage);
  return primary?.url || product.images?.[0]?.url || '';
}

function ensureStyles() {
  if (document.getElementById('aq-header-search-styles')) return;
  const style = document.createElement('style');
  style.id = 'aq-header-search-styles';
  style.textContent = `
    .aq-header-results{padding:12px 14px 18px;background:var(--color-background,#fff);color:var(--color-foreground,#111)}
    .aq-header-results__count{font-size:13px;opacity:.65;margin:2px 4px 10px}
    .aq-header-results__list{display:grid;gap:2px}
    .aq-header-result{display:grid;grid-template-columns:56px minmax(0,1fr);gap:12px;align-items:center;padding:8px;border-radius:8px;color:inherit;text-decoration:none}
    .aq-header-result:hover{background:rgb(var(--color-foreground-rgb,0 0 0)/.05)}
    .aq-header-result__image{width:56px;height:56px;object-fit:cover;border-radius:6px;background:#f4f4f4}
    .aq-header-result__fallback{width:56px;height:56px;border-radius:6px;background:#f4f4f4;display:grid;place-items:center;font-weight:700}
    .aq-header-result__title{font-weight:650;line-height:1.25}
    .aq-header-result__meta{font-size:12px;opacity:.65;margin-top:3px}
    .aq-header-results__all{display:block;margin:12px 4px 0;text-align:center;font-weight:650;color:inherit}
    .aq-header-results__empty{padding:24px 8px;text-align:center;opacity:.75}
  `;
  document.head.append(style);
}

function resultMarkup(product) {
  const image = productImage(product);
  const href = catalogueUrl(product.title);
  const media = image
    ? `<img class="aq-header-result__image" src="${escapeHtml(image)}" alt="" loading="lazy">`
    : `<div class="aq-header-result__fallback">${escapeHtml(String(product.title || '').slice(0, 2).toUpperCase())}</div>`;
  return `<a class="aq-header-result" href="${escapeHtml(href)}">
    ${media}
    <div>
      <div class="aq-header-result__title">${escapeHtml(product.title)}</div>
      <div class="aq-header-result__meta">${escapeHtml([product.vendor, product.productType].filter(Boolean).join(' · '))}</div>
    </div>
  </a>`;
}

function renderResults(component, query, products) {
  ensureStyles();
  const container = component.querySelector('[ref="predictiveSearchResults"]');
  if (!container) return;

  const needle = normalize(query);
  const matches = products.filter((product) => productText(product).includes(needle));
  const shown = matches.slice(0, MAX_RESULTS);

  if (!matches.length) {
    container.innerHTML = `<div class="aq-header-results" data-search-results>
      <div class="aq-header-results__empty">Nenhum produto encontrado para “${escapeHtml(query)}”.</div>
    </div>`;
    return;
  }

  container.innerHTML = `<div class="aq-header-results" data-search-results>
    <div class="aq-header-results__count">${matches.length} ${matches.length === 1 ? 'produto encontrado' : 'produtos encontrados'}</div>
    <div class="aq-header-results__list">${shown.map(resultMarkup).join('')}</div>
    <a class="aq-header-results__all" href="${escapeHtml(catalogueUrl(query))}">Ver todos os resultados</a>
  </div>`;
}

async function searchCatalogue(component, query) {
  if (!query) return;
  try {
    const products = await loadCatalogue();
    renderResults(component, query, products);
  } catch (error) {
    console.error('[AfroQueen header search]', error);
  }
}

function resetCustomSearch(component) {
  const input = component.querySelector('[ref="searchInput"]');
  const container = component.querySelector('[ref="predictiveSearchResults"]');
  const reset = component.querySelector('[ref="resetButton"]');
  if (input) input.value = '';
  if (reset) reset.hidden = true;
  if (container) container.innerHTML = '';
  input?.focus();
}

function isHeaderSearchInput(target) {
  return target instanceof HTMLInputElement && target.matches('#search-modal .search-input');
}

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!isHeaderSearchInput(target)) return;
  event.stopImmediatePropagation();

  const component = target.closest('predictive-search-component');
  if (!component) return;
  const reset = component.querySelector('[ref="resetButton"]');
  if (reset) reset.hidden = !target.value.trim();

  clearTimeout(searchTimer);
  const query = target.value.trim();
  if (!query) {
    const container = component.querySelector('[ref="predictiveSearchResults"]');
    if (container) container.innerHTML = '';
    return;
  }
  searchTimer = setTimeout(() => searchCatalogue(component, query), 120);
}, true);

document.addEventListener('keydown', (event) => {
  const target = event.target;
  if (!isHeaderSearchInput(target) || event.key !== 'Enter') return;
  const query = target.value.trim();
  if (!query) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.location.assign(catalogueUrl(query));
}, true);

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('#search-modal .predictive-search-form')) return;
  const input = form.querySelector('[ref="searchInput"]');
  const query = input?.value.trim();
  if (!query) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.location.assign(catalogueUrl(query));
}, true);

document.addEventListener('click', (event) => {
  const reset = event.target instanceof Element ? event.target.closest('#search-modal [ref="resetButton"]') : null;
  if (!reset) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const component = reset.closest('predictive-search-component');
  if (component) resetCustomSearch(component);
}, true);

function applyCatalogueUrlSearch() {
  const query = new URL(window.location.href).searchParams.get('aq-search')?.trim();
  if (!query) return;

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const browser = document.querySelector('afroqueen-catalogue-browser');
    const input = browser?.querySelector('[data-aq-search]');
    if (browser?.dataset.loaded === 'true' && input instanceof HTMLInputElement && typeof browser.apply === 'function') {
      input.value = query;
      browser.visibleCount = browser.pageSize;
      browser.apply();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.clearInterval(timer);
    } else if (attempts >= 80) {
      window.clearInterval(timer);
    }
  }, 125);
}

applyCatalogueUrlSearch();
